// Auth Google sans Firebase - Utilise l'API Google Identity Services

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
const API_URL = import.meta.env.VITE_API_URL || '/api';

console.log('[GoogleAuth] CLIENT_ID present:', !!CLIENT_ID);

export interface GoogleUser {
  email: string;
  name: string;
  picture: string;
  sub: string; // ID Google unique
}

// Charger le script Google Identity Services
export function loadGoogleScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    // Vérifier si déjà chargé
    if (document.getElementById('google-script') || window.google?.accounts) {
      console.log('[GoogleAuth] Script already loaded');
      resolve();
      return;
    }

    // Vérifier si on a un CLIENT_ID
    if (!CLIENT_ID) {
      console.error('[GoogleAuth] No CLIENT_ID provided');
      reject(new Error('VITE_GOOGLE_CLIENT_ID not configured'));
      return;
    }

    console.log('[GoogleAuth] Loading script...');
    const script = document.createElement('script');
    script.id = 'google-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('[GoogleAuth] Script loaded successfully');
      resolve();
    };
    script.onerror = () => {
      console.error('[GoogleAuth] Failed to load script');
      reject(new Error('Failed to load Google script'));
    };
    document.head.appendChild(script);
  });
}

// Initialiser le bouton Google
export function initGoogleButton(
  containerId: string,
  callback: (response: { credential: string }) => void
): void {
  if (!window.google) {
    console.error('[GoogleAuth] Google not available');
    throw new Error('Google not loaded');
  }

  if (!CLIENT_ID) {
    console.error('[GoogleAuth] No CLIENT_ID');
    throw new Error('CLIENT_ID not configured');
  }

  const container = document.getElementById(containerId);
  if (!container) {
    console.error('[GoogleAuth] Container not found:', containerId);
    throw new Error(`Container #${containerId} not found`);
  }

  console.log('[GoogleAuth] Initializing button...');

  try {
    window.google.accounts.id.initialize({
      client_id: CLIENT_ID,
      callback: (response) => {
        console.log('[GoogleAuth] Callback received');
        callback(response);
      },
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    window.google.accounts.id.renderButton(
      container,
      {
        theme: 'outline',
        size: 'large',
        width: '100%',
        text: 'continue_with',
        shape: 'rectangular',
      }
    );

    console.log('[GoogleAuth] Button rendered');
  } catch (error) {
    console.error('[GoogleAuth] Error initializing button:', error);
    throw error;
  }
}

// Prompt manuel (pour fallback)
export function promptGoogleSignIn(
  callback: (response: { credential: string }) => void
): void {
  if (!window.google) {
    console.error('[GoogleAuth] Google not available for prompt');
    return;
  }

  if (!CLIENT_ID) {
    console.error('[GoogleAuth] No CLIENT_ID for prompt');
    return;
  }

  console.log('[GoogleAuth] Prompting...');

  try {
    window.google.accounts.id.initialize({
      client_id: CLIENT_ID,
      callback: (response) => {
        console.log('[GoogleAuth] Prompt callback received');
        callback(response);
      },
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    window.google.accounts.id.prompt((notification: unknown) => {
      console.log('[GoogleAuth] Prompt notification:', notification);
    });
  } catch (error) {
    console.error('[GoogleAuth] Error prompting:', error);
  }
}

// Vérifier le token avec le backend (authentification côté serveur)
export async function verifyWithBackend(token: string): Promise<GoogleUser> {
  const res = await fetch(`${API_URL}/auth/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token }),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.detail || 'Token invalide ou non autorisé.');
  }

  const data = await res.json();
  return data.user as GoogleUser;
}

// Décoder le JWT token (utilisé uniquement pour récupérer la photo de profil côté client si on le souhaite,
// l'autorisation réelle est faite via le backend dans verifyWithBackend)
export function decodeJwt(token: string): GoogleUser | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const decoded = JSON.parse(jsonPayload);
    return decoded;
  } catch (error) {
    console.error('[GoogleAuth] Error decoding JWT:', error);
    return null;
  }
}

// Sauvegarder la session
export function saveSession(token: string, user: GoogleUser): void {
  localStorage.setItem('admin_token', token);
  localStorage.setItem('admin_user', JSON.stringify(user));
  console.log('[GoogleAuth] Session saved');
}

// Récupérer la session
export function getSession(): { token: string | null; user: GoogleUser | null } {
  const token = localStorage.getItem('admin_token');
  const userStr = localStorage.getItem('admin_user');
  
  if (!token || !userStr) {
    return { token: null, user: null };
  }

  try {
    const user = JSON.parse(userStr) as GoogleUser;
    return { token, user };
  } catch {
    clearSession();
    return { token: null, user: null };
  }
}

// Effacer la session
export function clearSession(): void {
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_user');
  console.log('[GoogleAuth] Session cleared');
}

// Déconnexion
export function logout(): void {
  clearSession();
  if (window.google?.accounts?.id) {
    window.google.accounts.id.disableAutoSelect();
  }
}

// Types pour Google
declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          renderButton: (
            element: HTMLElement,
            config: {
              theme?: 'outline' | 'filled_blue' | 'filled_black';
              size?: 'large' | 'medium' | 'small';
              width?: string | number;
              text?: 'signin_with' | 'continue_with' | 'signup_with';
              shape?: 'rectangular' | 'pill' | 'circle' | 'square';
            }
          ) => void;
          prompt: (callback?: (notification: unknown) => void) => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}
